const LAZY_VIDEO_SELECTOR = 'video[data-lazy-video]';
const NO_CONTROLS_VIDEO_SELECTOR = [
  'video[data-no-native-controls]',
  'video.hero__video',
  'video.slide__video',
  '.hero__video-wrapper video',
  '.slide__image-container video',
  'deferred-media video',
].join(', ');

/**
 * Removes any UI/attribute that would expose native video controls. Safe to call
 * many times on the same element.
 * @param {HTMLVideoElement} video
 */
function removeNativeVideoControls(video) {
  if (video.hasAttribute('controls')) video.removeAttribute('controls');
  if (video.controls) video.controls = false;
  video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback noplaybackrate');
  video.disablePictureInPicture = true;
  if ('disableRemotePlayback' in video) {
    try {
      video.disableRemotePlayback = true;
    } catch (_) {}
  }
}

/**
 * @param {Document | Element} [root]
 */
function stripControlsFromAll(root) {
  const scope = root && 'querySelectorAll' in root ? root : document;
  const videos = scope.querySelectorAll(NO_CONTROLS_VIDEO_SELECTOR);
  videos.forEach((video) => removeNativeVideoControls(/** @type {HTMLVideoElement} */ (video)));
}

/**
 * Watches the whole document so that any video added later (templates inside
 * <deferred-media>, sliders, theme-editor re-renders, etc.) gets its native
 * controls stripped immediately on insertion and whenever the `controls`
 * attribute is touched.
 */
function startControlsMutationObserver() {
  if (!('MutationObserver' in window)) return;
  if (/** @type {any} */ (window).__lazyVideoControlsObserver) return;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.target instanceof HTMLVideoElement) {
        const video = mutation.target;
        if (video.matches(NO_CONTROLS_VIDEO_SELECTOR)) {
          removeNativeVideoControls(video);
        }
        continue;
      }

      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLVideoElement) {
            if (node.matches(NO_CONTROLS_VIDEO_SELECTOR)) {
              removeNativeVideoControls(node);
            }
          } else if (node instanceof Element) {
            stripControlsFromAll(node);
          }
        });
      }
    }
  });

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['controls'],
  });

  /** @type {any} */ (window).__lazyVideoControlsObserver = observer;
}

/**
 * @param {HTMLVideoElement} video
 */
function activateLazyVideo(video) {
  if (video.dataset.lazyVideoActivated === 'true') return;
  video.dataset.lazyVideoActivated = 'true';

  for (const source of video.querySelectorAll('source[data-src]')) {
    const sourceEl = /** @type {HTMLSourceElement} */ (source);
    const url = sourceEl.dataset.src;
    if (!url) continue;
    sourceEl.src = url;
    sourceEl.removeAttribute('data-src');
  }

  video.load();
  removeNativeVideoControls(video);

  if (video.dataset.lazyAutoplay === 'true') {
    video.play().catch(() => {});
  }
}

function initLazyVideos() {
  const videos = document.querySelectorAll(LAZY_VIDEO_SELECTOR);
  if (!videos.length) return;

  if (!('IntersectionObserver' in window)) {
    videos.forEach(activateLazyVideo);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        activateLazyVideo(/** @type {HTMLVideoElement} */ (entry.target));
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '200px 0px' }
  );

  for (const video of videos) {
    observer.observe(video);
  }
}

function initVideos() {
  stripControlsFromAll(document);
  startControlsMutationObserver();
  initLazyVideos();
}

stripControlsFromAll(document);
startControlsMutationObserver();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideos);
} else {
  initVideos();
}

document.addEventListener('shopify:section:load', initVideos);
